import { useCallback, useEffect, useState } from 'react';
import { Pin, PinOff, Pencil, Trash2, Check, X, FileCheck } from 'lucide-react';
import StaffingModal from './StaffingModal';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useApiClient } from '@/context/ApiContext';
import { getNotes, createNote, updateNote, deleteNote } from '@/services/staffing/Staffing';
import { getUsersWithFilters } from '@/services/user/User';
import { Note } from '@/types/staffing/notes';

interface NotesModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    identifier: string;
    userType: 'client' | 'staff' | 'applicant';
    displayName: string;
    onCountChange: (count: number) => void;
}

const NotesModal = ({ open, onOpenChange, identifier, userType, displayName, onCountChange }: NotesModalProps) => {
    const apiClient = useApiClient();
    const [notes, setNotes] = useState<Note[]>([]);
    const [nameMap, setNameMap] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [newNoteText, setNewNoteText] = useState('');
    const [newNotePinned, setNewNotePinned] = useState(false);
    const [newNoteSendToFile, setNewNoteSendToFile] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const [editingId, setEditingId] = useState<number | null>(null);
    const [editText, setEditText] = useState('');
    const [editSaving, setEditSaving] = useState(false);

    const fetchNotes = useCallback(() => {
        setLoading(true);
        setError(null);
        getNotes(
            { filter: `UserType eq '${userType}' and UserIdentifier eq '${identifier}' and IsExpired eq false` },
            apiClient
        )
            .then(async (res) => {
                const fetched: Note[] = res.data.value ?? res.data;
                const sorted = [...fetched].sort((a, b) => {
                    if (a.Pinned !== b.Pinned) return a.Pinned ? -1 : 1;
                    return new Date(b.Created).getTime() - new Date(a.Created).getTime();
                });
                setNotes(sorted);
                onCountChange(sorted.length);

                const createdBys = Array.from(new Set(sorted.map(n => n.CreatedBy).filter(Boolean)));
                if (createdBys.length > 0) {
                    const stripDomain = (login: string) => login.includes('\\') ? login.split('\\')[1] : login;
                    const filter = createdBys.map(l => `LoginName eq '${stripDomain(l)}'`).join(' or ');
                    getUsersWithFilters({ filter }, apiClient)
                        .then(userRes => {
                            const users: { LoginName: string; FullName: string }[] = userRes.data.value ?? userRes.data;
                            const loginToName: Record<string, string> = {};
                            users.forEach(u => { loginToName[u.LoginName] = u.FullName; });
                            const map: Record<string, string> = {};
                            createdBys.forEach(cb => { map[cb] = loginToName[stripDomain(cb)] ?? cb; });
                            setNameMap(map);
                        })
                        .catch(() => {});
                }
            })
            .catch(() => setError('Failed to load notes.'))
            .finally(() => setLoading(false));
    }, [apiClient, identifier, userType, onCountChange]);

    useEffect(() => {
        if (open) fetchNotes();
    }, [open, fetchNotes]);

    const handleCreate = () => {
        if (!newNoteText.trim()) return;
        setSubmitting(true);
        createNote({ userIdentifier: identifier, userType, note: newNoteText.trim(), pinned: newNotePinned, sendToFile: newNoteSendToFile }, apiClient)
            .then(() => {
                setNewNoteText('');
                setNewNotePinned(false);
                setNewNoteSendToFile(false);
                fetchNotes();
            })
            .catch(() => setError('Failed to create note.'))
            .finally(() => setSubmitting(false));
    };

    const handlePinToggle = (note: Note) => {
        updateNote(note.Id, userType, { note: note.Note, pinned: !note.Pinned }, apiClient)
            .then(() => fetchNotes())
            .catch(() => setError('Failed to update note.'));
    };

    const handleEditSave = (note: Note) => {
        if (!editText.trim()) return;
        setEditSaving(true);
        updateNote(note.Id, userType, { note: editText.trim(), pinned: note.Pinned }, apiClient)
            .then(() => {
                setEditingId(null);
                fetchNotes();
            })
            .catch(() => setError('Failed to save note.'))
            .finally(() => setEditSaving(false));
    };

    const handleDelete = (note: Note) => {
        deleteNote(note.Id, userType, apiClient)
            .then(() => fetchNotes())
            .catch(() => setError('Failed to delete note.'));
    };

    const startEdit = (note: Note) => {
        setEditingId(note.Id);
        setEditText(note.Note);
    };

    const pinnedNotes = notes.filter(n => n.Pinned);
    const regularNotes = notes.filter(n => !n.Pinned);

    const renderNote = (note: Note) => (
        <div key={note.Id} className="border rounded-md p-3 flex flex-col gap-2">
            {editingId === note.Id ? (
                <>
                    <Textarea
                        value={editText}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEditText(e.target.value)}
                        rows={3}
                        className="resize-none"
                    />
                    <div className="flex gap-2 justify-end">
                        <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>
                            <X className="h-3 w-3 mr-1" /> Cancel
                        </Button>
                        <Button size="sm" onClick={() => handleEditSave(note)} disabled={editSaving || !editText.trim()}>
                            <Check className="h-3 w-3 mr-1" /> Save
                        </Button>
                    </div>
                </>
            ) : (
                <>
                    <p className="text-sm whitespace-pre-wrap">{note.Note}</p>
                    <div className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                            By {nameMap[note.CreatedBy] ?? note.CreatedBy} on {new Date(note.Created).toLocaleDateString()}
                            {note.SentToFile && (
                                <span title="Sent to file" className="inline-flex">
                                    <FileCheck className="h-3.5 w-3.5 text-muted-foreground" />
                                </span>
                            )}
                        </span>
                        <div className="flex gap-1">
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handlePinToggle(note)} title={note.Pinned ? 'Unpin' : 'Pin'}>
                                {note.Pinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
                            </Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => startEdit(note)} title="Edit">
                                <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => handleDelete(note)} title="Delete">
                                <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );

    return (
        <StaffingModal open={open} onOpenChange={onOpenChange} title={`Notes — ${displayName}`} wide>
            <div className="flex flex-col gap-4 p-1">
                <div className="flex flex-col gap-2 border rounded-md p-3 bg-muted/30">
                    <Textarea
                        placeholder="Add a new note..."
                        value={newNoteText}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNewNoteText(e.target.value)}
                        rows={3}
                        className="resize-none"
                    />
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                                <Checkbox
                                    id="pin-new"
                                    checked={newNotePinned}
                                    onCheckedChange={(v: boolean) => setNewNotePinned(v)}
                                />
                                <Label htmlFor="pin-new" className="text-sm cursor-pointer">Pin this note</Label>
                            </div>
                            <div className="flex items-center gap-2">
                                <Checkbox
                                    id="send-to-file-new"
                                    checked={newNoteSendToFile}
                                    onCheckedChange={(v: boolean) => setNewNoteSendToFile(v)}
                                />
                                <Label htmlFor="send-to-file-new" className="text-sm cursor-pointer">Send to file</Label>
                            </div>
                        </div>
                        <Button size="sm" onClick={handleCreate} disabled={submitting || !newNoteText.trim()}>
                            {submitting ? 'Adding...' : 'Add Note'}
                        </Button>
                    </div>
                </div>

                {error && <p className="text-sm text-destructive">{error}</p>}

                {loading ? (
                    <p className="text-sm text-muted-foreground">Loading notes...</p>
                ) : notes.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No notes yet.</p>
                ) : (
                    <div className="flex flex-col gap-4">
                        {pinnedNotes.length > 0 && (
                            <div className="flex flex-col gap-2">
                                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                                    <Pin className="h-3 w-3" /> Pinned
                                </p>
                                {pinnedNotes.map(renderNote)}
                            </div>
                        )}
                        {regularNotes.length > 0 && (
                            <div className="flex flex-col gap-2">
                                {pinnedNotes.length > 0 && (
                                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Notes</p>
                                )}
                                {regularNotes.map(renderNote)}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </StaffingModal>
    );
};

export default NotesModal;
